// conversationFlow — per-response accounting behind the always-on conversation
// flow log (ENG-907).
//
// Why this exists: the whole ENG-907 failure was invisible from the browser
// console. Every conversation log went through devLog, which no-ops unless
// ?debug=1 is set, so a normal load printed the socket connecting and then
// nothing — no prompt sent, no acknowledgement, no reply, and no hint that the
// reply had arrived carrying zero audio. "It answered but said nothing" and
// "it's just slow" looked identical, which is exactly the confusion the ticket
// opened with.
//
// So the flow gets a handful of always-on lines: what we sent, that the server
// acknowledged, each sentence back, its audio/frame counts, and a one-line
// summary. Handful is the point — this is per response, not per frame, so it
// stays off the hot path (per-sentence detail below the summary is still
// devLog-gated).
//
// The two failure modes are accounted for SEPARATELY because conflating them is
// what sends outage hunts down the wrong path:
//   silent   — text came back, no audio      → no voice ID for the character
//   faceless — audio came back, no ARKit     → no A2F endpoint, or A2F is down
//
// Only `silent` is warned about by the caller. `faceless` is computed here so the
// accounting is complete and testable, but a2fHealth.js owns that alarm — it
// judges per delivered sentence with a consecutive-sentence threshold and a
// dismissible viewport pill, which is strictly better than a per-response guess.
//
// Pure and injectable-clock; unit-tested in tests/unit/conversationFlowLog.test.mjs.

const num = (v) => (Number.isFinite(v) ? v : 0)

export class ConversationFlow {
  /** @param {() => number} clock injectable for tests */
  constructor(clock = () => Date.now()) {
    this._clock = clock
    this._reset()
  }

  _reset() {
    this._prompt = null
    this._startedAt = null
    // Sentence numbers seen, from TextOut OR from audio for a sentence whose
    // TextOut never arrived — otherwise a summary reads "0 sentences, 4000
    // bytes", which looks like a bug in the log rather than in the server.
    this._sentences = new Set()
    this._audioBytes = 0
    this._frames = 0
  }

  /** A prompt went out. Starts a fresh response. */
  sent(text) {
    this._reset()
    this._prompt = typeof text === 'string' ? text : null
    this._startedAt = this._clock()
    return this._prompt
  }

  /** Server acknowledged (NewResponse) — it is generating. */
  acknowledged(data) {
    return data?.trigger ?? null
  }

  /** One sentence of text came back. */
  textOut(data) {
    const n = data?.sentenceNum
    this._sentences.add(Number.isFinite(n) ? n : 0)
  }

  /** Audio/ARKit for a sentence. Called per StreamChunk, so it accumulates. */
  audio(data) {
    if (!data) return
    const n = data.sentenceNum
    this._sentences.add(Number.isFinite(n) ? n : 0)
    this._audioBytes += num(data.audioBytes)
    this._frames += num(data.frames)
  }

  /**
   * Summarize the response. Idempotent — chunks can trail ResponseComplete, so
   * calling again reflects whatever genuinely arrived late instead of throwing
   * or double-counting.
   */
  complete(data) {
    const sentences = data?.sentenceCount ?? this._sentences.size
    const audioBytes = this._audioBytes
    const frames = this._frames
    return {
      prompt: this._prompt,
      sentences: Number.isFinite(sentences) ? sentences : this._sentences.size,
      audioBytes,
      frames,
      elapsedMs: this._startedAt == null ? null : this._clock() - this._startedAt,
      // Text (or nothing) came back but no audio did.
      silent: audioBytes === 0,
      // Audio came back but no ARKit frames — a different subsystem.
      faceless: audioBytes > 0 && frames === 0,
    }
  }
}

/** Trim a prompt/reply for a single-line log without hiding that it was cut. */
export function clip(text, max = 120) {
  const s = text == null ? '' : String(text)
  return s.length <= max ? s : `${s.slice(0, max)}…(+${s.length - max})`
}
